import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  MdArrowBack, 
  MdPrint, 
  MdDownload, 
  MdEmail,
  MdReceipt,
  MdPerson,
  MdCalendarToday,
  MdPayment,
  MdAttachMoney,
  MdCancel,
  MdUndo
} from 'react-icons/md';
import { recibosAPI, pagosAPI, configuracionAPI } from '../services/api';
import { useModal } from '../context/ModalContext';
import { formatDate, formatCurrency } from '../utils/formatters';
import { generateThermalReceipt } from '../utils/thermalPdfUtils';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const ReceiptDetailContainer = styled.div`
  width: 100%;
  max-width: 100%;

  @media print {
    margin: 0 !important;
    padding: 0 !important;
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xl};
  gap: ${props => props.theme.spacing.md};
  flex-wrap: wrap;

  @media print {
    display: none !important;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.lg};
`;

const BackButton = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.colors.backgroundLight};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  color: ${props => props.theme.colors.textPrimary};
  transition: ${props => props.theme.transitions.fast};
  cursor: pointer;

  svg {
    font-size: 1.5rem;
  }

  &:hover {
    background-color: ${props => props.theme.colors.background};
    border-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primary};
  }
`;

const Title = styled.h1`
  font-size: ${props => props.theme.fontSizes.xxxl};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  flex-wrap: wrap;

  @media print {
    display: none !important;
  }
`;

const IconButton = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.colors.backgroundLight};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  color: ${props => props.theme.colors.textPrimary};
  transition: ${props => props.theme.transitions.fast};
  cursor: pointer;

  svg {
    font-size: 1.25rem;
  }

  &:hover {
    background-color: ${props => props.theme.colors.primary};
    border-color: ${props => props.theme.colors.primary};
    color: white;
  }
`;

const ReceiptCard = styled.div`
  background: ${props => props.theme.colors.backgroundLight};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xxl};
  box-shadow: ${props => props.theme.shadows.card};
  margin-bottom: ${props => props.theme.spacing.xl};

  @media print {
    background: white !important;
    border: none !important;
    box-shadow: none !important;
    border-radius: 0 !important;
    margin: 0 !important;
    padding: 20px !important;
    page-break-after: auto;
  }
`;

const ReceiptHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  padding-bottom: ${props => props.theme.spacing.xl};
  border-bottom: 2px solid ${props => props.theme.colors.border};
  margin-bottom: ${props => props.theme.spacing.xl};
  gap: ${props => props.theme.spacing.lg};
  flex-wrap: wrap;
`;

const CompanyInfo = styled.div`
  flex: 1;
`;

const CompanyLogo = styled.div`
  width: 80px;
  height: 80px;
  margin-bottom: ${props => props.theme.spacing.md};
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  
  @media print {
    width: 60px;
    height: 60px;
  }
`;

const CompanyName = styled.h2`
  font-size: ${props => props.theme.fontSizes.xxl};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 ${props => props.theme.spacing.sm} 0;
  font-weight: ${props => props.theme.fontWeights.bold};
`;

const CompanyDetails = styled.p`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  margin: ${props => props.theme.spacing.xs} 0;
  line-height: 1.6;
`;

const ReceiptInfo = styled.div`
  text-align: right;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    text-align: left;
  }
`;

const ReceiptNumber = styled.div`
  font-size: ${props => props.theme.fontSizes.xl};
  color: ${props => props.theme.colors.primary};
  font-weight: ${props => props.theme.fontWeights.bold};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const ReceiptDate = styled.div`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  margin: ${props => props.theme.spacing.xs} 0;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.semibold};
  margin-top: ${props => props.theme.spacing.sm};
  background-color: ${props => {
    if (props.status === 'Pagado') return props.theme.colors.success + '20';
    if (props.status === 'Vencido') return props.theme.colors.danger + '20';
    return props.theme.colors.warning + '20';
  }};
  color: ${props => {
    if (props.status === 'Pagado') return props.theme.colors.success;
    if (props.status === 'Vencido') return props.theme.colors.danger;
    return props.theme.colors.warning;
  }};
`;

const Section = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const SectionTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.lg};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 ${props => props.theme.spacing.md} 0;
  font-weight: ${props => props.theme.fontWeights.semibold};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};

  svg {
    color: ${props => props.theme.colors.primary};
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${props => props.theme.spacing.lg};
  background: ${props => props.theme.colors.background};
  padding: ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.md};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const InfoLabel = styled.span`
  font-size: ${props => props.theme.fontSizes.xs};
  color: ${props => props.theme.colors.textSecondary};
  font-weight: ${props => props.theme.fontWeights.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.span`
  font-size: ${props => props.theme.fontSizes.md};
  color: ${props => props.theme.colors.textPrimary};
  font-weight: ${props => props.theme.fontWeights.semibold};
`;

const ItemsTableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin: ${props => props.theme.spacing.lg} 0;
`;

const ItemsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 480px;

  @media print {
    page-break-inside: avoid;
    margin: 10px 0 !important;
  }
`;

const TableHeader = styled.thead`
  background-color: ${props => props.theme.colors.background};
`;

const Th = styled.th`
  padding: ${props => props.theme.spacing.md};
  text-align: ${props => props.align || 'left'};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.semibold};
  color: ${props => props.theme.colors.textSecondary};
  border-bottom: 2px solid ${props => props.theme.colors.border};
`;

const Td = styled.td`
  padding: ${props => props.theme.spacing.md};
  text-align: ${props => props.align || 'left'};
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textPrimary};
  border-bottom: 1px solid ${props => props.theme.colors.borderLight};
`;

const TotalsSection = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${props => props.theme.spacing.xl};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    justify-content: stretch;
  }
`;

const TotalsBox = styled.div`
  min-width: 350px;
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.lg};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    min-width: 0;
    width: 100%;
  }
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => props.theme.spacing.sm} 0;
  border-bottom: ${props => props.total ? '2px' : '1px'} solid ${props => props.theme.colors.border};

  &:last-child {
    border-bottom: none;
    padding-top: ${props => props.theme.spacing.md};
    margin-top: ${props => props.theme.spacing.sm};
  }
`;

const TotalLabel = styled.span`
  font-size: ${props => props.large ? props.theme.fontSizes.lg : props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  font-weight: ${props => props.large ? props.theme.fontWeights.bold : props.theme.fontWeights.medium};
`;

const TotalValue = styled.span`
  font-size: ${props => props.large ? props.theme.fontSizes.xxl : props.theme.fontSizes.md};
  color: ${props => props.large ? props.theme.colors.primary : props.theme.colors.textPrimary};
  font-weight: ${props => props.theme.fontWeights.bold};
`;

const PaymentHistory = styled.div`
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.lg};
`;

const PaymentItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => props.theme.spacing.md};
  border-bottom: 1px solid ${props => props.theme.colors.borderLight};

  &:last-child {
    border-bottom: none;
  }
`;

const PaymentInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const PaymentDate = styled.span`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textPrimary};
  font-weight: ${props => props.theme.fontWeights.semibold};
`;

const PaymentMethod = styled.span`
  font-size: ${props => props.theme.fontSizes.xs};
  color: ${props => props.theme.colors.textSecondary};
`;

const PaymentAmount = styled.span`
  font-size: ${props => props.theme.fontSizes.lg};
  color: ${props => props.theme.colors.success};
  font-weight: ${props => props.theme.fontWeights.bold};
`;

const NotesSection = styled.div`
  background: ${props => props.theme.colors.background};
  border-left: 4px solid ${props => props.theme.colors.primary};
  padding: ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.md};
  margin-top: ${props => props.theme.spacing.xl};
`;

const NotesText = styled.p`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.6;
  margin: 0;
`;

const Footer = styled.div`
  text-align: center;
  padding-top: ${props => props.theme.spacing.xl};
  border-top: 1px solid ${props => props.theme.colors.border};
  margin-top: ${props => props.theme.spacing.xl};
`;

const FooterText = styled.p`
  font-size: ${props => props.theme.fontSizes.xs};
  color: ${props => props.theme.colors.textLight};
  margin: ${props => props.theme.spacing.xs} 0;
`;

const CancelButton = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  background-color: ${props => props.isRefund ? props.theme.colors.warning : props.theme.colors.danger};
  color: white;
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.semibold};
  cursor: pointer;
  transition: ${props => props.theme.transitions.fast};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};

  &:hover {
    background-color: ${props => props.isRefund ? props.theme.colors.warningDark : props.theme.colors.dangerDark};
    transform: translateY(-2px);
  }

  &:disabled {
    background-color: ${props => props.theme.colors.textLight};
    cursor: not-allowed;
    transform: none;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(4px);
  padding: 20px;
  overflow-y: auto;
`;

const Modal = styled.div`
  background: ${props => props.theme.colors.backgroundLight};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: 20px;
  max-width: 450px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${props => props.theme.shadows.xl};
  border: 1px solid ${props => props.theme.colors.border};
  margin: auto;
  
  @media (max-width: 768px) {
    max-width: 95%;
    padding: 16px;
    max-height: 85vh;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid ${props => props.theme.colors.border};

  svg {
    font-size: 1.5rem;
    color: ${props => props.isRefund ? props.theme.colors.warning : props.theme.colors.danger};
    flex-shrink: 0;
  }
  
  @media (max-width: 768px) {
    gap: 8px;
    margin-bottom: 12px;
    padding-bottom: 8px;
    
    svg {
      font-size: 1.25rem;
    }
  }
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const ModalBody = styled.div`
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    margin-bottom: 12px;
  }
`;

const WarningText = styled.p`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.5;
  margin-bottom: 12px;
  
  @media (max-width: 768px) {
    font-size: 0.813rem;
    margin-bottom: 10px;
  }
`;

const HighlightBox = styled.div`
  background: ${props => props.isRefund ? props.theme.colors.warning + '20' : props.theme.colors.danger + '20'};
  border-left: 4px solid ${props => props.isRefund ? props.theme.colors.warning : props.theme.colors.danger};
  padding: 10px 12px;
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: 12px;
  
  p {
    margin: 4px 0;
    font-weight: ${props => props.theme.fontWeights.semibold};
    color: ${props => props.theme.colors.textPrimary};
    font-size: 0.875rem;
  }
  
  @media (max-width: 768px) {
    padding: 8px 10px;
    margin-bottom: 10px;
    
    p {
      font-size: 0.813rem;
      margin: 3px 0;
    }
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 70px;
  padding: 10px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 0.875rem;
  font-family: inherit;
  resize: vertical;
  transition: ${props => props.theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary + '20'};
  }
  
  @media (max-width: 768px) {
    min-height: 60px;
    padding: 8px;
    font-size: 0.813rem;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid ${props => props.hasError ? props.theme.colors.danger : props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 0.938rem;
  font-family: inherit;
  font-weight: ${props => props.theme.fontWeights.semibold};
  letter-spacing: 2px;
  text-align: center;
  text-transform: uppercase;
  transition: ${props => props.theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? props.theme.colors.danger : props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.hasError ? props.theme.colors.danger + '20' : props.theme.colors.primary + '20'};
  }

  &::placeholder {
    letter-spacing: normal;
    text-transform: none;
    font-weight: normal;
  }
  
  @media (max-width: 768px) {
    padding: 8px;
    font-size: 0.875rem;
  }
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.danger};
  background-color: ${props => props.theme.colors.danger + '10'};
  border: 1px solid ${props => props.theme.colors.danger};
  border-radius: ${props => props.theme.borderRadius.sm};
  padding: 8px 10px;
  font-size: 0.813rem;
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    padding: 6px 8px;
    font-size: 0.75rem;
  }
  
  &:before {
    content: '⚠️';
  }
`;

const InfoMessage = styled.div`
  color: ${props => props.theme.colors.info};
  background-color: ${props => props.theme.colors.info + '10'};
  border: 1px solid ${props => props.theme.colors.info};
  border-radius: ${props => props.theme.borderRadius.sm};
  padding: 8px 10px;
  font-size: 0.813rem;
  margin-top: 6px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    padding: 6px 8px;
    font-size: 0.75rem;
    margin-bottom: 6px;
  }
  
  &:before {
    content: 'ℹ️';
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 16px;
  
  @media (max-width: 768px) {
    gap: 8px;
    margin-top: 12px;
  }
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 0.875rem;
  font-weight: ${props => props.theme.fontWeights.semibold};
  cursor: pointer;
  transition: ${props => props.theme.transitions.fast};
  border: none;

  ${props => props.variant === 'primary' ? `
    background-color: ${props.isRefund ? props.theme.colors.warning : props.theme.colors.danger};
    color: white;
    
    &:hover {
      background-color: ${props.isRefund ? props.theme.colors.warningDark : props.theme.colors.dangerDark};
    }
  ` : `
    background-color: ${props.theme.colors.background};
    color: ${props.theme.colors.textPrimary};
    border: 1px solid ${props.theme.colors.border};
    
    &:hover {
      background-color: ${props.theme.colors.backgroundLight};
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 0.813rem;
  }
`;

const ReceiptDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useModal();
  
  const [recibo, setRecibo] = useState(null);
  const [pagos, setPagos] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelMotivo, setCancelMotivo] = useState('');
  const [cancelCodigo, setCancelCodigo] = useState('');
  const [codigoError, setCodigoError] = useState('');
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    loadReceiptData();
  }, [id]);

  const loadReceiptData = async () => {
    try {
      setLoading(true);
      
      // Cargar configuración de plantillas
      const configResponse = await configuracionAPI.getConfiguracion();
      setConfig(configResponse.data.data);
      
      // Cargar datos del recibo
      const reciboResponse = await recibosAPI.getById(id);
      setRecibo(reciboResponse.data.data);
      
      // Cargar pagos del recibo
      try {
        const pagosResponse = await pagosAPI.getAll();
        // Filtrar pagos de este recibo
        const pagosFiltrados = pagosResponse.data.data.filter(p => p.recibo_id == id);
        setPagos(pagosFiltrados);
      } catch (err) {
        console.log('No hay pagos o error al cargar:', err);
        setPagos([]);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error al cargar recibo:', err);
      setError(err.message || 'Error al cargar el recibo');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    handleGeneratePDF(true); // true = imprimir
  };

  const handleDownload = () => {
    handleGeneratePDF(false); // false = descargar
  };

  const handleGeneratePDF = (print = false) => {
    try {
      // Verificar si usar formato térmico
      const useThermal = config?.formato_impresion === 'termica';
      
      if (useThermal) {
        // Usar formato térmico (ticket)
        const paperWidth = parseInt(config?.ancho_papel_termico) || 80;
        generateThermalReceipt(recibo, config, pagos, paperWidth, print);
        return;
      }
      
      // Formato normal A4
      // Crear documento PDF en orientación vertical (portrait)
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      let yPosition = 12;

      // Configuración de la empresa
      const empresa = config?.nombre_empresa || 'Mi Empresa';
      const direccion = config?.direccion || '';
      const telefono = config?.telefono || '';
      const email = config?.email_contacto || '';
      const logoUrl = config?.logo_url || '';

      // Encabezado mejorado con logo y nombre de empresa
      const headerStartY = yPosition;
      
      // Agregar logo si existe (lado izquierdo)
      if (logoUrl && logoUrl.startsWith('data:image')) {
        try {
          const logoSize = 20; // Tamaño del logo en mm
          const logoX = 20; // Posición izquierda
          
          doc.addImage(logoUrl, 'PNG', logoX, yPosition, logoSize, logoSize);
        } catch (error) {
          console.error('Error al agregar logo:', error);
        }
      }

      // Información de la empresa (lado derecho del logo o centrado si no hay logo)
      const textX = logoUrl && logoUrl.startsWith('data:image') ? 45 : pageWidth / 2;
      const textAlign = logoUrl && logoUrl.startsWith('data:image') ? 'left' : 'center';
      
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(empresa, textX, yPosition + 5, { align: textAlign });
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      let infoY = yPosition + 10;
      
      if (direccion) {
        doc.text(direccion, textX, infoY, { align: textAlign });
        infoY += 4;
      }
      if (telefono || email) {
        const contacto = [telefono, email].filter(Boolean).join(' | ');
        doc.text(contacto, textX, infoY, { align: textAlign });
        infoY += 4;
      }
      
      // Ajustar yPosition al final del encabezado
      yPosition = Math.max(headerStartY + 20, infoY);

      // Línea separadora
      yPosition += 3;
      doc.setDrawColor(200, 200, 200);
      doc.line(15, yPosition, pageWidth - 15, yPosition);
      yPosition += 6;

      // Título del documento - más compacto
      doc.setFontSize(12); // Reducido de 16 a 12
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(91, 155, 213); // Color primario
      doc.text('RECIBO DE PAGO', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 6; // Reducido de 10 a 6

      // Información del recibo en dos columnas - COMPACTADO
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      
      const leftCol = 20;
      const rightCol = pageWidth / 2 + 10;
      const lineHeight = 6;

      // Columna izquierda
      doc.text('No. Recibo:', leftCol, yPosition);
      doc.setFont('helvetica', 'normal');
      doc.text(recibo.numero_recibo || 'N/A', leftCol + 25, yPosition);
      
      doc.setFont('helvetica', 'bold');
      doc.text('Fecha:', leftCol, yPosition + lineHeight);
      doc.setFont('helvetica', 'normal');
      doc.text(formatDate(recibo.fecha_emision), leftCol + 25, yPosition + lineHeight);
      
      doc.setFont('helvetica', 'bold');
      doc.text('Vencimiento:', leftCol, yPosition + lineHeight * 2);
      doc.setFont('helvetica', 'normal');
      doc.text(formatDate(recibo.fecha_vencimiento), leftCol + 25, yPosition + lineHeight * 2);

      // Columna derecha
      doc.setFont('helvetica', 'bold');
      doc.text('Estado:', rightCol, yPosition);
      doc.setFont('helvetica', 'normal');
      const statusBadge = getStatusBadge(recibo.estado);
      doc.setTextColor(parseInt(statusBadge.color.slice(1, 3), 16), 
                       parseInt(statusBadge.color.slice(3, 5), 16), 
                       parseInt(statusBadge.color.slice(5, 7), 16));
      doc.text(statusBadge.text.toUpperCase(), rightCol + 20, yPosition);
      doc.setTextColor(0, 0, 0);
      
      doc.setFont('helvetica', 'bold');
      doc.text('Método Pago:', rightCol, yPosition + lineHeight);
      doc.setFont('helvetica', 'normal');
      doc.text(recibo.metodo_pago || 'N/A', rightCol + 28, yPosition + lineHeight);

      yPosition += lineHeight * 2 + 8;

      // Información del cliente - CONTENEDOR GRIS
      const containerMargin = 15; // Mismo margen que la tabla
      const containerWidth = pageWidth - (containerMargin * 2); // Mismo ancho que la tabla
      
      doc.setFillColor(245, 245, 245);
      doc.rect(containerMargin, yPosition - 3, containerWidth, 20, 'F');
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text('CLIENTE', containerMargin + 5, yPosition);
      yPosition += 5;

      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      
      // Nombre y RFC (truncar si es muy largo para evitar desbordamiento)
      const maxWidth = containerWidth - 10; // Dejar margen interno
      let clienteInfo = recibo.cliente_nombre || 'N/A';
      if (recibo.cliente_rfc) {
        clienteInfo += ` - RFC: ${recibo.cliente_rfc}`;
      }
      
      // Truncar texto si es muy largo
      const clienteInfoLines = doc.splitTextToSize(clienteInfo, maxWidth);
      doc.text(clienteInfoLines[0], containerMargin + 5, yPosition);
      yPosition += 4;
      
      // Contacto en una línea (truncar si es necesario)
      if (recibo.cliente_email || recibo.cliente_telefono) {
        const contactInfo = [recibo.cliente_email, recibo.cliente_telefono].filter(Boolean).join(' | ');
        const contactInfoLines = doc.splitTextToSize(contactInfo, maxWidth);
        doc.text(contactInfoLines[0], containerMargin + 5, yPosition);
        yPosition += 4;
      }
      yPosition += 6;

      // Tabla de conceptos - MÁS COMPACTA
      const tableData = (recibo.items || recibo.conceptos || []).map(concepto => [
        concepto.descripcion || 'N/A',
        concepto.cantidad?.toString() || '1',
        formatCurrency(concepto.precio_unitario || 0),
        formatCurrency((concepto.cantidad || 1) * (concepto.precio_unitario || 0))
      ]);

      autoTable(doc, {
        startY: yPosition,
        head: [['Concepto', 'Cant.', 'P. Unit.', 'Total']],
        body: tableData,
        theme: 'striped',
        headStyles: {
          fillColor: [91, 155, 213],
          textColor: 255,
          fontStyle: 'bold',
          halign: 'center',
          fontSize: 9
        },
        columnStyles: {
          0: { cellWidth: 100, halign: 'left' },
          1: { cellWidth: 25, halign: 'center' },
          2: { cellWidth: 30, halign: 'right' },
          3: { cellWidth: 30, halign: 'right' }
        },
        styles: {
          fontSize: 9,
          cellPadding: 3
        },
        margin: { left: 15, right: 15 }
      });

      yPosition = doc.lastAutoTable.finalY + 8;

      // Totales
      const rightAlign = pageWidth - 20;
      const labelX = rightAlign - 50;
      const valueX = rightAlign - 5;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text('Subtotal:', labelX, yPosition, { align: 'right' });
      doc.text(formatCurrency(recibo.subtotal || 0), valueX, yPosition, { align: 'right' });
      yPosition += 6;

      if (recibo.monto_iva && recibo.monto_iva > 0) {
        doc.text(`IVA (${recibo.iva || 16}%):`, labelX, yPosition, { align: 'right' });
        doc.text(formatCurrency(recibo.monto_iva), valueX, yPosition, { align: 'right' });
        yPosition += 6;
      }

      if (recibo.monto_descuento && recibo.monto_descuento > 0) {
        doc.setTextColor(220, 53, 69); // Rojo para descuento
        doc.text(`Descuento (${recibo.descuento || 0}%):`, labelX, yPosition, { align: 'right' });
        doc.text(`-${formatCurrency(recibo.monto_descuento)}`, valueX, yPosition, { align: 'right' });
        doc.setTextColor(0, 0, 0);
        yPosition += 6;
      }

      // Línea antes del total
      doc.setDrawColor(200, 200, 200);
      doc.line(labelX - 50, yPosition, rightAlign, yPosition);
      yPosition += 6;

      // Total
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text('TOTAL:', labelX, yPosition, { align: 'right' });
      doc.text(formatCurrency(recibo.monto_total || 0), valueX, yPosition, { align: 'right' });

      // Historial de pagos si existen - MÁS COMPACTO
      if (pagos && pagos.length > 0) {
        yPosition += 8; // Reducido de 15 a 8
        
        // Solo mostrar si hay espacio suficiente (más de 50mm disponibles)
        const espacioDisponible = pageHeight - yPosition - 15; // 15mm para pie de página
        
        if (espacioDisponible > 50) {
          doc.setFontSize(9); // Reducido de 11 a 9
          doc.setFont('helvetica', 'bold');
          doc.text('HISTORIAL DE PAGOS', 20, yPosition);
          yPosition += 5; // Reducido de 8 a 5

          const pagosData = pagos.slice(0, 3).map(pago => [ // Limitar a 3 pagos máximo
            formatDate(pago.fecha_pago),
            (pago.metodo_pago || 'N/A').substring(0, 15), // Truncar si es muy largo
            (pago.referencia || '-').substring(0, 20),
            formatCurrency(pago.monto || 0)
          ]);

          autoTable(doc, {
            startY: yPosition,
            head: [['Fecha', 'Método', 'Referencia', 'Monto']],
            body: pagosData,
            theme: 'plain',
            headStyles: {
              fillColor: [245, 245, 245],
              textColor: 0,
              fontStyle: 'bold',
              fontSize: 7 // Reducido
            },
            columnStyles: {
              0: { cellWidth: 28 },
              1: { cellWidth: 35 },
              2: { cellWidth: 55 },
              3: { cellWidth: 30, halign: 'right' }
            },
            styles: {
              fontSize: 7, // Reducido de 9 a 7
              cellPadding: 1.5 // Reducido de 3 a 1.5
            },
            margin: { left: 15, right: 15 }
          });

          yPosition = doc.lastAutoTable.finalY + 3; // Reducido de 5 a 3
        }
      }

      // Notas si existen - SOLO SI HAY ESPACIO
      const espacioRestante = pageHeight - yPosition - 12; // 12mm para pie de página
      
      if (recibo.notas && espacioRestante > 15) {
        yPosition += 5; // Reducido de 10 a 5
        
        doc.setFontSize(7); // Reducido de 9 a 7
        doc.setFont('helvetica', 'bold');
        doc.text('Notas:', 20, yPosition);
        yPosition += 3; // Reducido de 5 a 3
        
        doc.setFont('helvetica', 'normal');
        // Limitar longitud de notas para que quepan
        const maxNotasLength = Math.floor(espacioRestante / 3.5); // Aproximado líneas disponibles
        const notasText = recibo.notas.length > maxNotasLength * 80 
          ? recibo.notas.substring(0, maxNotasLength * 80) + '...'
          : recibo.notas;
        const notasLines = doc.splitTextToSize(notasText, pageWidth - 40);
        doc.text(notasLines, 20, yPosition);
      }

      // Pie de página - SIEMPRE AL FINAL
      const footerY = pageHeight - 10; // Reducido de 15 a 10
      doc.setFontSize(7); // Reducido de 8 a 7
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(128, 128, 128);
      doc.text('Este es un documento generado electrónicamente', pageWidth / 2, footerY, { align: 'center' });
      doc.text(`Generado el ${new Date().toLocaleDateString('es-MX')}`, pageWidth / 2, footerY + 3, { align: 'center' }); // Reducido espacio de 4 a 3

      // Marca de agua si está cancelado o reembolsado
      if (recibo.estado === 'cancelado' || recibo.estado === 'reembolsado') {
        doc.setFontSize(60);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 0, 0, 0.2); // Rojo semi-transparente
        
        // Rotar el texto en diagonal
        const textWidth = doc.getTextWidth(recibo.estado.toUpperCase());
        const centerX = pageWidth / 2;
        const centerY = pageHeight / 2;
        
        // Guardar estado actual
        doc.saveGraphicsState();
        
        // Rotar 45 grados alrededor del centro de la página
        doc.setGState(new doc.GState({ opacity: 0.15 }));
        
        // Aplicar transformación para rotar el texto
        const angle = -45 * Math.PI / 180; // -45 grados en radianes
        const cosAngle = Math.cos(angle);
        const sinAngle = Math.sin(angle);
        
        // Mover al centro, rotar, dibujar texto
        doc.text(
          recibo.estado.toUpperCase(),
          centerX,
          centerY,
          {
            align: 'center',
            angle: -45,
            renderingMode: 'stroke'
          }
        );
        
        // Restaurar estado
        doc.restoreGraphicsState();
        
        // Resetear color y opacidad
        doc.setTextColor(0, 0, 0);
      }

      // Imprimir o Descargar PDF
      if (print) {
        // Abrir en nueva ventana para imprimir
        window.open(doc.output('bloburl'), '_blank');
      } else {
        // Descargar PDF
        const fileName = `Recibo_${recibo.folio || recibo.id}_${new Date().getTime()}.pdf`;
        doc.save(fileName);
      }

    } catch (error) {
      console.error('Error al generar PDF:', error);
      toast('Error al generar el PDF. Por favor intente nuevamente.', 'error');
    }
  };

  const handleEmail = () => {
    if (recibo?.cliente_email) {
      toast(`Enviando recibo a: ${recibo.cliente_email}`, 'info');
    } else {
      toast('El cliente no tiene email registrado', 'warning');
    }
  };

  const handleCancelClick = () => {
    setShowCancelModal(true);
    setCodigoError('');
    setCancelCodigo('');
  };

  const handleCancelConfirm = async () => {
    // Validar código de confirmación (configurable en Configuración → Pagos y Recibos)
    const codigoEsperado = (config?.codigo_cancelacion_recibos || 'DICO161').toUpperCase();

    if (cancelCodigo.trim().toUpperCase() !== codigoEsperado) {
      setCodigoError('Código incorrecto. Contacte al administrador si no lo conoce.');
      return;
    }

    try {
      setCancelling(true);
      setCodigoError('');
      
      await recibosAPI.cancelar(id, {
        motivo: cancelMotivo || 'Sin motivo especificado',
        usuario: 'Usuario Actual' // Aquí podrías usar el usuario logueado
      });

      // Recargar datos del recibo
      await loadReceiptData();
      
      setShowCancelModal(false);
      setCancelMotivo('');
      setCancelCodigo('');
      toast(`Recibo ${isRefundable ? 'reembolsado' : 'cancelado'} exitosamente`, 'success');

    } catch (error) {
      console.error('Error al cancelar recibo:', error);
      toast(error.message || 'Error al cancelar el recibo', 'error');
    } finally {
      setCancelling(false);
    }
  };

  const handleCancelModalClose = () => {
    if (!cancelling) {
      setShowCancelModal(false);
      setCancelMotivo('');
      setCancelCodigo('');
      setCodigoError('');
    }
  };
  
  const getStatusBadge = (estado) => {
    const estados = {
      'pagado': { color: '#4CAF50', text: 'Pagado' },
      'pendiente': { color: '#FFC107', text: 'Pendiente' },
      'parcial': { color: '#FF9800', text: 'Parcial' },
      'vencido': { color: '#F44336', text: 'Vencido' },
      'cancelado': { color: '#9E9E9E', text: 'Cancelado' },
      'reembolsado': { color: '#FF5722', text: 'Reembolsado' }
    };
    return estados[estado?.toLowerCase()] || { color: '#757575', text: estado };
  };

  if (loading) {
    return (
      <ReceiptDetailContainer>
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <p>Cargando datos del recibo...</p>
        </div>
      </ReceiptDetailContainer>
    );
  }

  if (error || !recibo) {
    return (
      <ReceiptDetailContainer>
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <p style={{ color: '#f44336' }}>{error || 'Recibo no encontrado'}</p>
          <button onClick={() => navigate('/recibos')} style={{ marginTop: '20px' }}>
            Volver a Recibos
          </button>
        </div>
      </ReceiptDetailContainer>
    );
  }

  // Calcular totales
  const totalPagado = pagos.reduce((sum, pago) => sum + parseFloat(pago.monto || 0), 0);
  const saldoPendiente = parseFloat(recibo.saldo_pendiente || 0);
  
  // Determinar si es reembolsable (tiene pagos) o solo cancelable
  const isRefundable = recibo.estado === 'pagado' || totalPagado > 0;
  const isCancellable = recibo.estado !== 'cancelado' && recibo.estado !== 'reembolsado';

  return (
    <ReceiptDetailContainer>
      <PageHeader>
        <HeaderLeft>
          <BackButton onClick={() => navigate(-1)}>
            <MdArrowBack />
          </BackButton>
          <Title>Detalle del Recibo</Title>
        </HeaderLeft>
        
        <ActionButtons>
          <IconButton onClick={handlePrint} title="Imprimir">
            <MdPrint />
          </IconButton>
          <IconButton onClick={handleDownload} title="Descargar PDF">
            <MdDownload />
          </IconButton>
          <IconButton onClick={handleEmail} title="Enviar por Email">
            <MdEmail />
          </IconButton>
          {isCancellable && (
            <CancelButton 
              onClick={handleCancelClick} 
              isRefund={isRefundable}
              title={isRefundable ? 'Reembolsar Recibo' : 'Cancelar Recibo'}
            >
              {isRefundable ? <MdUndo /> : <MdCancel />}
              {isRefundable ? 'Reembolsar' : 'Cancelar'}
            </CancelButton>
          )}
        </ActionButtons>
      </PageHeader>

      <ReceiptCard>
        <ReceiptHeader>
          <CompanyInfo>
            {config?.plantilla_recibo_mostrar_logo && config?.logo_url && (
              <CompanyLogo>
                <img src={config.logo_url} alt="Logo de la empresa" />
              </CompanyLogo>
            )}
            <CompanyName>{config?.nombre_empresa || 'Payment Manager'}</CompanyName>
            <CompanyDetails>
              {config?.plantilla_recibo_mostrar_direccion && config?.direccion && (
                <>📍 {config.direccion}<br /></>
              )}
              {config?.plantilla_recibo_mostrar_telefono && config?.telefono && (
                <>📞 {config.telefono}<br /></>
              )}
              {config?.plantilla_recibo_mostrar_email && config?.email_contacto && (
                <>✉️ {config.email_contacto}<br /></>
              )}
            </CompanyDetails>
          </CompanyInfo>
          
          <ReceiptInfo>
            <ReceiptNumber>
              <MdReceipt style={{ verticalAlign: 'middle', marginRight: '8px' }} />
              {recibo.numero_recibo}
            </ReceiptNumber>
            <ReceiptDate>
              <strong>Fecha de Emisión:</strong> {formatDate(recibo.fecha_emision, config?.formato_fecha)}
            </ReceiptDate>
            <ReceiptDate>
              <strong>Fecha de Vencimiento:</strong> {formatDate(recibo.fecha_vencimiento, config?.formato_fecha)}
            </ReceiptDate>
            <StatusBadge status={recibo.estado} >
              {getStatusBadge(recibo.estado).text}
            </StatusBadge>
          </ReceiptInfo>
        </ReceiptHeader>

        <Section>
          <SectionTitle>
            <MdPerson />
            Información del Cliente
          </SectionTitle>
          <InfoGrid>
            <InfoItem>
              <InfoLabel>Nombre / Empresa</InfoLabel>
              <InfoValue>{recibo.cliente_nombre}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>RFC</InfoLabel>
              <InfoValue>{recibo.cliente_rfc || 'N/A'}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Email</InfoLabel>
              <InfoValue>{recibo.cliente_email || 'N/A'}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Teléfono</InfoLabel>
              <InfoValue>{recibo.cliente_telefono || 'N/A'}</InfoValue>
            </InfoItem>
            {(recibo.cliente_direccion || recibo.cliente_ciudad) && (
              <InfoItem style={{ gridColumn: '1 / -1' }}>
                <InfoLabel>Dirección</InfoLabel>
                <InfoValue>
                  {[recibo.cliente_direccion, recibo.cliente_ciudad, recibo.cliente_codigo_postal, recibo.cliente_pais]
                    .filter(Boolean)
                    .join(', ')}
                </InfoValue>
              </InfoItem>
            )}
          </InfoGrid>
        </Section>

        <Section>
          <SectionTitle>
            <MdReceipt />
            Conceptos / Servicios
          </SectionTitle>
          <ItemsTableWrapper>
          <ItemsTable>
            <TableHeader>
              <tr>
                <Th>Descripción</Th>
                <Th align="center">Cantidad</Th>
                <Th align="right">Precio Unit.</Th>
                <Th align="right">Total</Th>
              </tr>
            </TableHeader>
            <tbody>
              {recibo.items && recibo.items.length > 0 ? (
                recibo.items.map(item => (
                  <tr key={item.id}>
                    <Td>{item.descripcion}</Td>
                    <Td align="center">{item.cantidad}</Td>
                    <Td align="right">${parseFloat(item.precio_unitario).toFixed(2)}</Td>
                    <Td align="right">${parseFloat(item.subtotal).toFixed(2)}</Td>
                  </tr>
                ))
              ) : (
                <tr>
                  <Td colSpan="4" align="center" style={{ padding: '20px', color: '#757575' }}>
                    {recibo.descripcion || 'Sin items detallados'}
                  </Td>
                </tr>
              )}
            </tbody>
          </ItemsTable>
          </ItemsTableWrapper>

          <TotalsSection>
            <TotalsBox>
              <TotalRow>
                <TotalLabel>Subtotal:</TotalLabel>
                <TotalValue>{formatCurrency(recibo.subtotal || 0, config?.formato_moneda)}</TotalValue>
              </TotalRow>
              {recibo.monto_iva > 0 && (
                <TotalRow>
                  <TotalLabel>IVA ({recibo.iva || 16}%):</TotalLabel>
                  <TotalValue>{formatCurrency(recibo.monto_iva || 0, config?.formato_moneda)}</TotalValue>
                </TotalRow>
              )}
              <TotalRow>
                <TotalLabel large>TOTAL:</TotalLabel>
                <TotalValue large>{formatCurrency(recibo.monto_total || 0, config?.formato_moneda)}</TotalValue>
              </TotalRow>
              {totalPagado > 0 && (
                <>
                  <TotalRow style={{ borderTop: '1px dashed #e0e0e0', paddingTop: '8px', marginTop: '8px' }}>
                    <TotalLabel>Total Pagado:</TotalLabel>
                    <TotalValue style={{ color: '#4CAF50' }}>{formatCurrency(totalPagado, config?.formato_moneda)}</TotalValue>
                  </TotalRow>
                  <TotalRow>
                    <TotalLabel>Saldo Pendiente:</TotalLabel>
                    <TotalValue style={{ color: saldoPendiente > 0 ? '#F44336' : '#4CAF50' }}>
                      {formatCurrency(saldoPendiente, config?.formato_moneda)}
                    </TotalValue>
                  </TotalRow>
                </>
              )}
            </TotalsBox>
          </TotalsSection>
        </Section>

        {pagos.length > 0 && (
          <Section>
            <SectionTitle>
              <MdPayment />
              Historial de Pagos ({pagos.length})
            </SectionTitle>
            <PaymentHistory>
              {pagos.map(pago => (
                <PaymentItem key={pago.id}>
                  <PaymentInfo>
                    <PaymentDate>
                      <MdCalendarToday style={{ fontSize: '0.875rem', verticalAlign: 'middle', marginRight: '4px' }} />
                      {formatDate(pago.fecha_pago, config?.formato_fecha)}
                    </PaymentDate>
                    <PaymentMethod>
                      {pago.metodo_pago} {pago.referencia ? `- Ref: ${pago.referencia}` : ''}
                    </PaymentMethod>
                  </PaymentInfo>
                  <PaymentAmount>
                    <MdAttachMoney style={{ fontSize: '1.25rem', verticalAlign: 'middle' }} />
                    {formatCurrency(pago.monto, config?.formato_moneda)}
                  </PaymentAmount>
                </PaymentItem>
              ))}
            </PaymentHistory>
          </Section>
        )}

        {/* Notas adicionales personalizadas */}
        {config?.plantilla_recibo_notas_adicionales && (
          <NotesSection>
            <SectionTitle style={{ marginBottom: '8px' }}>
              📝 Notas Adicionales
            </SectionTitle>
            <NotesText>{config.plantilla_recibo_notas_adicionales}</NotesText>
          </NotesSection>
        )}

        {/* Términos y condiciones personalizados */}
        {config?.plantilla_recibo_terminos && (
          <NotesSection>
            <SectionTitle style={{ marginBottom: '8px' }}>
              📋 Términos y Condiciones
            </SectionTitle>
            <NotesText>{config.plantilla_recibo_terminos}</NotesText>
          </NotesSection>
        )}

        {/* Notas específicas del recibo (si existen) */}
        {recibo.notas && (
          <NotesSection>
            <SectionTitle style={{ marginBottom: '8px' }}>
              📌 Notas del Recibo
            </SectionTitle>
            <NotesText>{recibo.notas}</NotesText>
          </NotesSection>
        )}

        <Footer>
          <FooterText>
            {config?.plantilla_recibo_pie_pagina || 'Este documento ha sido generado electrónicamente y es válido sin firma.'}
          </FooterText>
          <FooterText>
            Payment Manager - Sistema de Gestión de Pagos
          </FooterText>
        </Footer>
      </ReceiptCard>

      {/* Modal de Cancelación/Reembolso */}
      {showCancelModal && (
        <ModalOverlay onClick={handleCancelModalClose}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader isRefund={isRefundable}>
              {isRefundable ? <MdUndo /> : <MdCancel />}
              <ModalTitle>
                {isRefundable ? 'Reembolsar Recibo' : 'Cancelar Recibo'}
              </ModalTitle>
            </ModalHeader>

            <ModalBody>
              <WarningText>
                {isRefundable 
                  ? '⚠️ Este recibo tiene pagos registrados. Al reembolsarlo, se marcará como reembolsado y no podrá revertirse.'
                  : '⚠️ Está a punto de cancelar este recibo. Esta acción no se puede deshacer.'
                }
              </WarningText>

              <HighlightBox isRefund={isRefundable}>
                <p>Recibo: {recibo.numero_recibo}</p>
                <p>Cliente: {recibo.cliente_nombre}</p>
                <p>Monto: {formatCurrency(recibo.monto_total)}</p>
                {isRefundable && (
                  <p>Monto pagado: {formatCurrency(totalPagado)}</p>
                )}
              </HighlightBox>

              <label htmlFor="motivo" style={{ 
                display: 'block', 
                marginBottom: '6px',
                fontWeight: 600,
                fontSize: '0.875rem'
              }}>
                Motivo (opcional):
              </label>
              <TextArea
                id="motivo"
                value={cancelMotivo}
                onChange={(e) => setCancelMotivo(e.target.value)}
                placeholder={`Ej: ${isRefundable ? 'Devolución solicitada' : 'Error en facturación'}`}
                disabled={cancelling}
              />

              <label htmlFor="codigo" style={{ 
                display: 'block', 
                marginTop: '12px',
                marginBottom: '6px',
                fontWeight: 600,
                fontSize: '0.875rem'
              }}>
                Código de confirmación <span style={{ color: '#F44336' }}>*</span>:
              </label>
              <InfoMessage>
                Ingrese el código de seguridad del administrador.
              </InfoMessage>
              <Input
                id="codigo"
                type="password"
                value={cancelCodigo}
                onChange={(e) => {
                  setCancelCodigo(e.target.value);
                  setCodigoError('');
                }}
                placeholder="Código de seguridad"
                disabled={cancelling}
                hasError={!!codigoError}
                autoComplete="off"
              />
              {codigoError && <ErrorMessage>{codigoError}</ErrorMessage>}
            </ModalBody>

            <ModalActions>
              <Button 
                onClick={handleCancelModalClose}
                disabled={cancelling}
              >
                Cancelar
              </Button>
              <Button 
                variant="primary" 
                onClick={handleCancelConfirm}
                disabled={cancelling || !cancelCodigo.trim()}
                isRefund={isRefundable}
              >
                {cancelling ? 'Procesando...' : (isRefundable ? 'Confirmar Reembolso' : 'Confirmar Cancelación')}
              </Button>
            </ModalActions>
          </Modal>
        </ModalOverlay>
      )}
    </ReceiptDetailContainer>
  );
};

export default ReceiptDetail;
